/**
 * I18n Store Actions
 *
 * Contains all action implementations for the i18n store.
 *
 * @module @store/i18n/i18nActions
 */

import type { SupportedLanguage } from '@shared/i18n/types/i18nTypes';
import {
    DEFAULT_LANGUAGE,
    SUPPORTED_LANGUAGES,
} from '@shared/i18n/constants/languages';
import { loadTranslations } from '@shared/i18n/utils/loadTranslations';
import type { I18nActions, I18nState } from './i18nTypes';

/**
 * Initial state for reset operations.
 */
function getInitialState(): Pick<I18nState, 'language' | 'translations'> {
    return {
        language: DEFAULT_LANGUAGE,
        translations: {
            [SUPPORTED_LANGUAGES.NB]: {},
            [SUPPORTED_LANGUAGES.NN]: {},
            [SUPPORTED_LANGUAGES.EN_GB]: {},
        },
    };
}

/**
 * Creates the i18n actions object.
 *
 * @param set - Zustand set function
 * @returns I18nActions object
 */
export function createI18nActions(
    set: (
        partial:
            Partial<I18nState> | ((state: I18nState) => Partial<I18nState>),
    ) => void,
): I18nActions {
    return {
        /**
         * Sets the language.
         * @param language - The language to set
         */
        setLanguage: (language: SupportedLanguage): void => {
            // Save preference to localStorage
            localStorage.setItem('dynee-language', language);
            set({ language });
        },

        /**
         * Loads translations asynchronously.
         */
        loadTranslations: async (): Promise<void> => {
            set({ isLoading: true, error: null });
            try {
                const translations = await loadTranslations();
                set({
                    translations,
                    isLoading: false,
                });
            } catch (error) {
                set({
                    error:
                        error instanceof Error
                            ? error
                            : new Error('Failed to load translations'),
                    isLoading: false,
                });
            }
        },

        /**
         * Resets the i18n state to initial values.
         */
        reset: (): void => {
            localStorage.removeItem('dynee-language');
            set({
                ...getInitialState(),
                isLoading: false,
                error: null,
            });
        },
    };
}
